import React from 'react'
import { Link } from 'react-router-dom'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import logoIcon from '../../assets/logo-icon.png'
import Card from './Card'

export const ONBOARDING_STEPS = ['account', 'kyc', 'esign', 'done'] as const
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]

function StepIndicator({ current }: { current: OnboardingStep }) {
  const labels: Record<OnboardingStep, string> = {
    account: 'Account',
    kyc: 'KYC',
    esign: 'eSign',
    done: 'Done',
  }
  const idx = ONBOARDING_STEPS.indexOf(current)

  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {ONBOARDING_STEPS.filter(s => s !== 'done').map((step, i) => (
        <React.Fragment key={step}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors
              ${i < idx ? 'bg-green-500 text-white' : i === idx ? 'bg-primary-500 text-white' : 'bg-dark-700 text-gray-400'}`}
          >
            {i < idx ? <CheckCircleIcon className="w-5 h-5" /> : i + 1}
          </div>
          <span className={`text-xs hidden sm:block ${i <= idx ? 'text-gray-200' : 'text-gray-500'}`}>
            {labels[step]}
          </span>
          {i < ONBOARDING_STEPS.length - 2 && <div className={`h-px w-8 ${i < idx ? 'bg-green-500' : 'bg-dark-700'}`} />}
        </React.Fragment>
      ))}
    </div>
  )
}

type OnboardingLayoutProps = {
  currentStep: OnboardingStep
  children: React.ReactNode
  subtitle?: React.ReactNode
  showSteps?: boolean
  title?: string
  footerText?: string
}

const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({
  currentStep,
  children,
  subtitle,
  showSteps = true,
  title = 'Complete your profile',
  footerText = 'Your information is secure and encrypted in transit and at rest.',
}) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-950 py-12 px-4">
      <div className="max-w-lg w-full space-y-6">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center space-x-3 group mb-4">
            <img
              src={logoIcon}
              alt="AnalyzerView"
              className="w-12 h-12 object-contain group-hover:scale-105 transition-transform duration-300"
            />
            <div className="flex flex-col items-start">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                AnalyzerView
              </h1>
              <p className="text-xs text-gray-400 font-medium tracking-wide">RESEARCH PLATFORM</p>
            </div>
          </Link>
        </div>

        {showSteps && <StepIndicator current={currentStep} />}

        <Card className="!bg-dark-800 !rounded-2xl shadow-xl">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-100">{title}</h2>
            {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
          </div>

          {children}
        </Card>

        <p className="text-xs text-center text-gray-500">
          {footerText}
        </p>
      </div>
    </div>
  )
}

export default OnboardingLayout
